import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import EmergencyWarning from '../components/EmergencyWarning';
import {
  Calendar,
  MessageSquare,
  Pill,
  Heart,
  Clock,
  ArrowRight,
  Video,
  FileText,
  Bell,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appointmentsRes, messagesRes] = await Promise.all([
          axios.get(`${API_URL}/api/appointments`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/api/messages`, { headers: getAuthHeaders() })
        ]);
        setAppointments(appointmentsRes.data.slice(0, 3));
        setMessages(messagesRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAuthHeaders]);

  const quickActions = [
    {
      icon: Calendar,
      title: 'Book Appointment',
      description: 'Schedule a visit',
      href: '/book-appointment',
      color: 'bg-blue-500'
    },
    {
      icon: Video,
      title: 'Virtual Visit',
      description: 'Start telehealth',
      href: '/book-appointment?type=virtual',
      color: 'bg-green-500'
    },
    {
      icon: MessageSquare,
      title: 'Send Message',
      description: 'Contact care team',
      href: '/messages',
      color: 'bg-purple-500'
    },
    {
      icon: Pill,
      title: 'Refill Request',
      description: 'Request medication',
      href: '/pharmacy',
      color: 'bg-orange-500'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12" data-testid="patient-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-muted-foreground">
            Here's an overview of your health portal
          </p>
        </div>

        {/* Emergency Warning - Compact */}
        <div className="mb-8">
          <EmergencyWarning compact />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {quickActions.map((action, index) => (
            <Link key={index} to={action.href}>
              <Card className="h-full hover:shadow-md transition-shadow cursor-pointer group">
                <CardContent className="p-4 flex flex-col items-center text-center">
                  <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <action.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-sm">{action.title}</h3>
                  <p className="text-xs text-muted-foreground">{action.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - 2 cols */}
          <div className="lg:col-span-2 space-y-8">
            {/* Upcoming Appointments */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Upcoming Appointments
                </CardTitle>
                <Link to="/appointments">
                  <Button variant="ghost" size="sm" className="text-primary">
                    View All
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : appointments.length > 0 ? (
                  <div className="space-y-4">
                    {appointments.map((apt) => (
                      <div 
                        key={apt.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                            {apt.visit_type === 'virtual' ? (
                              <Video className="w-6 h-6 text-primary" />
                            ) : (
                              <Calendar className="w-6 h-6 text-primary" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium capitalize">{apt.visit_type.replace('-', ' ')} Visit</p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(apt.appointment_date)} at {apt.appointment_time}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(apt.status)}`}>
                          {apt.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-4">No upcoming appointments</p>
                    <Link to="/book-appointment">
                      <Button className="btn-primary">
                        Book Now
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Messages */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Recent Messages
                </CardTitle>
                <Link to="/messages">
                  <Button variant="ghost" size="sm" className="text-primary">
                    View All
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : messages.length > 0 ? (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`p-4 rounded-lg border ${!msg.read ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-transparent'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{msg.subject}</p>
                            <p className="text-sm text-muted-foreground line-clamp-1">{msg.content}</p>
                          </div>
                          {!msg.read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-4">No messages yet</p>
                    <Link to="/messages">
                      <Button variant="outline">
                        Send a Message
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - 1 col */}
          <div className="space-y-6">
            {/* AI Assistant Card */}
            <Card className="bg-gradient-to-br from-primary to-accent text-white">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-lg mb-2">AI Health Assistant</h3>
                <p className="text-white/80 text-sm mb-4">
                  Chat with our AI to assess symptoms, get care recommendations, or answer questions.
                </p>
                <Link to="/chatbot">
                  <Button variant="secondary" className="w-full bg-white text-primary hover:bg-white/90">
                    Start Chat
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Programs Card */}
            <Card>
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Wellness Programs</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Explore our optimization and transformation programs.
                </p>
                <Link to="/programs">
                  <Button variant="outline" className="w-full">
                    Explore Programs
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link 
                  to="/profile" 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm">Update Profile</span>
                </Link>
                <Link 
                  to="/insurance" 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm">Insurance Info</span>
                </Link>
                <Link 
                  to="/pharmacy" 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Pill className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm">Pharmacy & Delivery</span>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDashboard;
